import type { ComponentProps } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import type { BorderTopProps, FlexProps } from 'styled-system';
import { alignItems, borderTop, flex, space, themeGet } from 'styled-system';

import { Button } from 'common/components/Button';
import { Flex } from 'common/components/Flex';
import LoadButton from 'common/components/LoadButton/LoadButton';
import RadioCard from 'common/components/Radio/RadioCard';
import Spinner from 'common/components/Spinner';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { OverflowYProps } from 'common/theme/system-utilities';
import { overflowY } from 'common/theme/system-utilities';
import type { ContactFormOrder } from '../../types';
import { OrderDrawerHeader } from './OrderDrawerHeader';
import OrderLabel from './OrderLabel';

interface OrderListProps {
  orderIdToScroll: string;
  orders: ContactFormOrder[];
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  setOrder: (orderNumber: string, orderId: string) => void;
  fetchMore: () => Promise<void>;
  hasMoreOrders: boolean;
  hasFetchError?: boolean;
}

const RADIO_NAME = 'order';

const OrdersContainer = styled(Flex)<FlexProps & OverflowYProps>`
  ${overflowY};
`;
const ordersContainerStyles: ComponentProps<typeof OrdersContainer> = {
  flexDirection: 'column',
  px: ['sp_16', null, null, 'sp_20'],
  mb: ['sp_16', null, 'sp_20', 'sp_24'],
  flex: 1,
  overflowY: 'auto',
};

const StyledRadioCard = styled(RadioCard)`
  ${space};

  &:last-child {
    margin-bottom: ${themeGet('space.sp_8')}px;
  }
`;

const Footer = styled(Flex)<BorderTopProps>`
  ${borderTop};

  box-shadow: 0 1px 8px 0 ${themeGet('colors.overlay')};
`;

const footerStyles = {
  px: 'sp_16',
  py: 'sp_8',
  borderTop: 3,
};

const SelectButton = styled(Button)<FlexProps>`
  ${flex};
`;

const LoadButtonWrapper = styled(Flex)<FlexProps>`
  ${alignItems};
  ${space};
`;

export const OrderList = ({
  orderIdToScroll,
  setOrder,
  orders,
  isOpen,
  isLoading,
  onClose,
  fetchMore,
  hasMoreOrders,
  hasFetchError,
}: OrderListProps) => {
  const { t } = useLocalization();
  const [selectedOrder, setSelectedOrder] = useState(orderIdToScroll);
  const selectedOrderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (orderIdToScroll) {
      if (isOpen && orderIdToScroll !== selectedOrder) {
        setSelectedOrder(orderIdToScroll);
      }
    }
  }, [isOpen, orderIdToScroll]);

  useEffect(() => {
    if (selectedOrderRef.current) {
      selectedOrderRef.current.scrollIntoView({ block: 'center' });
    }
  }, []);

  const onSelectClick = () => {
    const order = orders.find(({ id, oms }) => (oms?.orderId ?? id) === selectedOrder);
    if (order) {
      setOrder(selectedOrder, order.id);
      onClose();
    }
  };

  return (
    <Flex flexDirection="column" height="100%">
      <OrdersContainer role="radiogroup" {...ordersContainerStyles}>
        <OrderDrawerHeader orders={orders} isLoading={isLoading} hasFetchError={hasFetchError} />
        {isLoading && <Spinner size={32} margin="sp_12" strokeColor="sys.neutral.border.hover" />}
        {orders.map(order => {
          const { id, oms } = order;
          const orderId = oms?.orderId ?? id;
          const isSelected = selectedOrder === orderId;
          const orderHeadline = `${t('h24_order_number')}: ${orderId}`;

          return (
            <StyledRadioCard
              key={orderId}
              name={RADIO_NAME}
              label={orderHeadline}
              value={orderId}
              checked={isSelected}
              onChange={setSelectedOrder}
              ref={isSelected ? selectedOrderRef : null}
            >
              <OrderLabel {...order} orderHeadline={orderHeadline} />
            </StyledRadioCard>
          );
        })}
        {hasMoreOrders && (
          <LoadButtonWrapper flexDirection="column" alignItems="center" my="sp_48">
            <LoadButton isRounded={true} isLoading={isLoading} onClick={fetchMore}>
              {t('h24_load_more')}
            </LoadButton>
          </LoadButtonWrapper>
        )}
      </OrdersContainer>

      <Footer {...footerStyles}>
        <SelectButton onClick={onSelectClick} flex="1" disabled={!selectedOrder}>
          {t('h24_select_this_order')}
        </SelectButton>
      </Footer>
    </Flex>
  );
};
